/**
 * Three.js Scene Setup
 * Creates the WebGL canvas with floating particles and geometric elements
 */

import * as THREE from 'three';

class WebGLScene {
  constructor() {
    this.canvas = document.getElementById('webgl-canvas');
    this.scene = new THREE.Scene();
    this.particles = null;
    this.geometries = [];
    this.mouse = { x: 0, y: 0 };
    this.targetMouse = { x: 0, y: 0 };
    this.clock = new THREE.Clock();
    
    this.init();
  }
  
  init() {
    this.setupCamera();
    this.setupRenderer();
    this.createParticles();
    this.createGeometries();
    this.setupEventListeners();
    this.animate();
  }
  
  setupCamera() {
    const aspect = window.innerWidth / window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
    this.camera.position.z = 50;
  }
  
  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true,
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }
  
  getCircleTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    
    // Soft glow circle
    const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.5)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 32, 32);
    
    const texture = new THREE.CanvasTexture(canvas);
    return texture;
  }

  createParticles() {
    // 1. Adaptive Grid Density based on device
    const isMobile = window.innerWidth < 768;
    
    // Desktop: 250x150 = 37,500 particles (High Fidelity)
    // Mobile: 100x80 = 8,000 particles (Performance Optimized)
    const segmentsX = isMobile ? 100 : 250;
    const segmentsZ = isMobile ? 80 : 150;
    
    const particleCount = segmentsX * segmentsZ;
    const spacing = 1.6;
    
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    
    for (let x = 0; x < segmentsX; x++) {
      for (let z = 0; z < segmentsZ; z++) {
        const i = (x * segmentsZ + z) * 3;
        positions[i] = (x - segmentsX / 2) * spacing;
        positions[i + 1] = 0; 
        positions[i + 2] = (z - segmentsZ / 2) * spacing;
      }
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    // 2. Custom Shader Material
    const material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uMouse: { value: new THREE.Vector2(0, 0) },
        uScroll: { value: 1.0 },
        uTexture: { value: this.getCircleTexture() }
      },
      vertexShader: `
        uniform float uTime;
        uniform vec2 uMouse;
        varying float vY;
        varying float vDistance;

        void main() {
          vec3 pos = position;
          
          // 1. Base Wave Logic (SLOWER for premium feel)
          float wave = sin(pos.x * 0.08 + uTime * 0.4) * 6.0;
          wave += cos(pos.z * 0.05 + uTime * 0.5) * 8.0;
          wave += sin((pos.x + pos.z) * 0.03 + uTime * 0.3) * 5.0;
          
          pos.y = wave;

          // 2. Mouse Interactivity (Tactile Displacement)
          // We project for NDC to check cursor proximity
          vec4 mvPosInitial = modelViewMatrix * vec4(pos, 1.0);
          vec4 screenPos = projectionMatrix * mvPosInitial;
          vec2 ndcPos = screenPos.xy / screenPos.w;
          float distToMouse = distance(ndcPos, uMouse);
          vDistance = distToMouse;

          // Local "Bulge" effect
          float influence = smoothstep(0.6, 0.0, distToMouse);
          pos.y += influence * 12.0; // Lift particles
          
          vY = pos.y; 

          // 3. Final Projection
          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          
          // 4. Depth & Height-based scaling
          float size = 650.0 / -mvPosition.z; 
          gl_PointSize = size * mix(0.2, 0.8, (pos.y + 16.0) / 32.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform float uScroll;
        varying float vY;
        varying float vDistance;

        void main() {
          // Gradient: Rich Red -> Vibrant Neon Red -> Electric Highlight
          vec3 colorDeep = vec3(0.5, 0.0, 0.0);    
          vec3 colorPeak = vec3(1.0, 0.05, 0.1);   
          vec3 colorHighlight = vec3(1.0, 0.9, 0.95); 
          
          float h = smoothstep(-15.0, 15.0, vY);
          vec3 finalColor = mix(colorDeep, colorPeak, h);
          
          finalColor = mix(finalColor, colorHighlight, pow(h, 6.0) * 0.7);
          
          // Cursor glow
          float glow = 1.0 - smoothstep(0.0, 0.4, vDistance);
          finalColor += glow * vec3(0.6, 0.15, 0.15);
          
          // Scroll-linked opacity
          float alpha = mix(0.5, 1.0, h) * 0.9 * uScroll;
          vec4 tex = texture2D(uTexture, gl_PointCoord);
          
          gl_FragColor = vec4(finalColor, alpha * tex.a);
        }
      `
    });

    this.particlesMaterial = material;
    this.particles = new THREE.Points(geometry, material);
    
    this.particles.rotation.x = -Math.PI * 0.22; 
    this.particles.position.y = -6; 
    
    this.scene.add(this.particles);
  }
  
  createGeometries() {
    this.geometries = []; 
  }
  
  setupEventListeners() {
    window.addEventListener('resize', this.onResize.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    window.addEventListener('scroll', this.onScroll.bind(this));
  }
  
  onResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }
  
  onScroll() {
    const scrollY = window.scrollY;
    const height = window.innerHeight;
    const progress = Math.min(scrollY / height, 1.0);
    
    if (this.particlesMaterial) {
      this.particlesMaterial.uniforms.uScroll.value = 1.0 - progress * 0.85;
    }
  }

  onMouseMove(event) {
    this.targetMouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.targetMouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  }
  
  animate() {
    requestAnimationFrame(this.animate.bind(this));
    
    const elapsedTime = this.clock.getElapsedTime();
    
    this.mouse.x += (this.targetMouse.x - this.mouse.x) * 0.05;
    this.mouse.y += (this.targetMouse.y - this.mouse.y) * 0.05;
    
    if (this.particlesMaterial) {
      this.particlesMaterial.uniforms.uTime.value = elapsedTime;
      this.particlesMaterial.uniforms.uMouse.value.set(this.mouse.x, this.mouse.y);
    }
    
    this.camera.position.x += (this.mouse.x * 2.5 - this.camera.position.x) * 0.05;
    this.camera.position.y += (this.mouse.y * 2.5 - this.camera.position.y) * 0.05;
    this.camera.lookAt(this.scene.position);
    
    this.renderer.render(this.scene, this.camera);
  }
}

// Initialize only when DOM is ready
// Initialize only when DOM is ready (Removed auto-init to prevent double instantiation from main.js)
// document.addEventListener('DOMContentLoaded', () => {
//   if (document.getElementById('webgl-canvas')) {
//     new WebGLScene();
//   }
// });

export default WebGLScene;
